import ConversationRecord from "models/view-models/conversations/conversation-record";
import ServiceFactory from "../service-factory";
import axios from "axios";
import ConversationMessageRecord from "models/view-models/conversations/conversation-message-record";
import { ResultRecord, ServiceResponse } from "@rsm-hcd/javascript-core";
import { MessageRole } from "models/enumerations/conversations/message-role";
import ConversationApplicationContextRecord from "models/view-models/conversations/conversation-application-context-record";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

export interface ConversationListQueryParams {
    searchText?: string;
    skip?: number;
    take?: number;
}

export interface ConversationResourcePathParams {
    id: string;
}

export interface RenameConversationParams {
    title: string;
}

export interface PinConversationParams {
    isPinned: boolean;
}

export interface DeleteConversationServiceParams {
    guid: string;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const baseEndpoint = "conversations";
const resourceEndpoint = `${baseEndpoint}/:id`;
const pinResourceEndpoint = `${baseEndpoint}/:id/pin`;
const resourceType = ConversationRecord;

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Service
// -------------------------------------------------------------------------------------------------

interface CreateConversationRequest {
    conversationId?: string;
    parentId?: string;
    messages: Array<{
        role: MessageRole;
        content: string;
    }>;
    applicationContext?: ConversationApplicationContextRecord;
}

const ConversationService = {
    createOrContinue: (
        request: CreateConversationRequest,
        signal: AbortSignal
    ) => {
        const readableStream = new ReadableStream<
            ServiceResponse<ConversationMessageRecord>
        >({
            async start(controller) {
                try {
                    const response = await axios.post(baseEndpoint, request, {
                        headers: {
                            Accept: "text/event-stream",
                        },
                        responseType: "stream",
                        adapter: "fetch",
                        signal: signal,
                    });

                    if (!(response.data instanceof ReadableStream)) {
                        throw new Error(
                            "Response data is not a stream. Ensure the endpoint supports streaming."
                        );
                    }

                    const reader = response.data.getReader();
                    const decoder = new TextDecoder();
                    let buffer = "";

                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;

                        buffer += decoder.decode(value, { stream: true });

                        let split: number;
                        while ((split = buffer.indexOf("\n\n")) !== -1) {
                            const chunk = buffer.slice(0, split);
                            buffer = buffer.slice(split + 2);

                            if (!chunk.startsWith("data:")) continue;

                            const data = chunk.slice(5).trim();

                            if (data === "[DONE]") {
                                return;
                            }

                            const json = JSON.parse(data);

                            controller.enqueue({
                                result: new ResultRecord<ConversationMessageRecord>(
                                    {
                                        resultObject: json,
                                    }
                                ),
                                resultObject: new ConversationMessageRecord(
                                    json
                                ),
                                resultObjects: [],
                                rowCount: 1,
                                status: response.status,
                            });
                        }
                    }
                } catch (error) {
                    if (axios.isCancel(error)) {
                        controller.error(
                            new Error("Request was cancelled by the user.")
                        );
                    } else {
                        controller.error(error);
                    }
                } finally {
                    controller.close();
                }
            },
        });

        return readableStream;
    },

    create: ServiceFactory.create<ConversationRecord>(
        resourceType,
        baseEndpoint
    ),

    delete: ServiceFactory.delete(resourceEndpoint),

    list: ServiceFactory.list<ConversationRecord, ConversationListQueryParams>(
        resourceType,
        baseEndpoint
    ),
    get: ServiceFactory.get<ConversationRecord, ConversationResourcePathParams>(
        resourceType,
        resourceEndpoint
    ),
    rename: ServiceFactory.patch<
        Pick<ConversationRecord, "id" | "title">,
        ConversationResourcePathParams,
        RenameConversationParams
    >(resourceType, resourceEndpoint),
    pin: ServiceFactory.patch<
        Pick<ConversationRecord, "id" | "isPinned">,
        ConversationResourcePathParams,
        PinConversationParams
    >(resourceType, pinResourceEndpoint),
};

// #endregion Service

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export default ConversationService;

// #endregion Exports
